import { TabulatorFull } from "tabulator-tables";



import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { dataEvents } from "@/data/events/dataEvents";
import type { CancellationState, PaymentProvider, PaymentState } from "@/data/model/Enums";
import type { ITransactionMethodSplit } from "@/data/model/Order";
import { DateTime } from "@/data/util/DateTime";



import { logError } from "@/shared/logging/errors";
import { logWarning } from "@/shared/logging/informations";
import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";
import { toMoneyWithSign } from "@/shared/utils/format";



import { Popup } from "@/components/dialog/Popup";



import { OperatorClient } from "@/operator/OperatorClient";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { CancellationStateBadge } from "@/operator/elements/CancellationStateBadge";
import { PaymentStateBadge } from "@/operator/elements/transaction/PaymentStateBadge";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { OrderDetailsModal } from "@/operator/modal/OrderDetailsModal";
import { TransactionDetailsWindow } from "@/operator/modal/TransactionDetailsWindow";
import { UserMenu } from "@/operator/ui/UserMenu";
import { FulfillmentSoundNotifications, SoundNotifications } from "@/operator/ux/SoundNotifications";



import { BaseController, ViewTransition } from "./BaseController";


class PaymentTableRow {
    constructor(
        public id: string,
        public paymentId: string,
        public date: string,
        public state: PaymentState | undefined,
        public orders: PaymentOrderCell[],
        public tableName: string,
        public totalIncTip: string,
        public paymentMethod: PaymentMethodCell | undefined,
        public cancellationState?: CancellationState,
        public userName?: string
    ) {
        this.id = id;
        this.paymentId = paymentId;
        this.date = date;
        this.orders = orders;
        this.state = state;
        this.cancellationState = cancellationState;
        this.tableName = tableName;
        this.totalIncTip = totalIncTip;
        this.paymentMethod = paymentMethod;
        this.userName = userName;
    }
}

export class PaymentMethodCell {
    constructor(
        public state: PaymentState,
        public provider?: PaymentProvider,
        public brand?: string,
        public type?: string,
        public region?: string,
        public holder?: string,
        public ccDigits?: string,
        public ccExpiryYear?: number,
        public ccExpiryMonth?: number,
        public customPaymentMethodImagePath?: string,
        public predefinedPaymentMethodImagePath?: string,
        public actualPaymentMethodImagePath?: string
    ) {
        this.state = state;
        this.provider = provider;
        this.brand = brand;
        this.type = type;
        this.region = region;
        this.holder = holder;
        this.ccDigits = ccDigits;
        this.ccExpiryYear = ccExpiryYear;
        this.ccExpiryMonth = ccExpiryMonth;
        this.customPaymentMethodImagePath = customPaymentMethodImagePath;
        this.predefinedPaymentMethodImagePath = predefinedPaymentMethodImagePath;
        this.actualPaymentMethodImagePath = actualPaymentMethodImagePath;
    }
}

class PaymentOrderCell {
    constructor(
        public id: string,
        public number: string,
        public orderIncrement?: string
    ) {
        this.id = id;
        this.number = number;
        this.orderIncrement = orderIncrement;
    }
}

export class PaymentsController extends BaseController {
    private productionContext: ProductionContext;

    private readonly $head: JQuery;
    private readonly $scrollable: JQuery;
    private readonly $filterHeader: JQuery;

    private readonly $ordersSearch: JQuery;
    private readonly $searchButton: JQuery;
    private readonly $paymentsTable: JQuery;

    private $scrollableTable?: JQuery = undefined;

    private readonly itemsPageSize = 30;
    private table?: any;
    private data: object[] = [];
    private readonly orderDao: OrderDao;

    private searchedOrderName: string = "";
    private currentPage = 0;
    private hasNextPage = false;
    private isDataLoading = false;
    private readonly createdFromLastBusinessDays = 3;

    constructor() {
        super("payments");

        this.productionContext = ProductionContext.getInstance();

        this.orderDao = Factory.getInstance<OrderDao>(OrderDao);

        this.$head = $('<div class="head"><div class="logo">tastier</div></div>').appendTo(this.$view);
        new UserMenu().appendTo(this.$head);

        this.$scrollable = $('<div class="scrollable"></div>');
        this.$scrollable.appendTo(this.$view);

        this.$filterHeader = $('<div class="filter-header" style="margin: 16px;"></div>').appendTo(this.$scrollable);
        const firstRow = $('<div class="row"></div>').appendTo(this.$filterHeader);

        this.$ordersSearch = $(
            `<input type="text" name="name" class="form-control col-md" placeholder="${trans.items.searchTerm}"/>`
        ).appendTo($('<div class="col-md-3"></div>').appendTo(firstRow));

        this.$searchButton = $(`<button class="btn btn-success">${trans.items.search}</button>`).appendTo(
            $('<div class="col-md"></div>').appendTo(firstRow)
        );
        this.$searchButton.bind("click.b", () => {
            this.searchedOrderName = this.$ordersSearch.val();
            this.reloadData();
        });

        this.$ordersSearch.keypress((event) => {
            const keycode = event.keyCode ? event.keyCode : event.which;
            if (keycode == 13) {
                // the enter key code
                this.searchedOrderName = this.$ordersSearch.val();
                this.reloadData();
                return false;
            }
        });

        this.$paymentsTable = $('<div id="payments-table"></div>').appendTo(this.$scrollable);
    }

    public showView(transition?: ViewTransition) {
        this.show(transition);

        this.initializeTable();
    }

    protected onBecomeVisible() {
        operatorEvents.productionContext.FulfillmentAdded.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        dataEvents.operatorSocket.OrderCancelled.add(this.refreshDataWithDelay, this);
        dataEvents.operatorSocket.RoomPlanChanged.add(this.refreshDataWithDelay, this);
    }

    protected onBecomeInvisible() {
        this.table = undefined;
        this.isDataLoading = false;
        this.data = [];
        operatorEvents.productionContext.FulfillmentAdded.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);

        dataEvents.operatorSocket.OrderCancelled.remove(this.refreshDataWithDelay, this);
        dataEvents.operatorSocket.RoomPlanChanged.remove(this.refreshDataWithDelay, this);
    }

    private refreshDataWithDelay() {
        setTimeout(() => this.reloadData(0), 1500);
    }

    private initializeTable() {
        const that = this;
        if (this.table === undefined) {
            this.table = new TabulatorFull("#payments-table", {
                maxHeight: "100%",
                height: "calc(100% - 80px)",
                data: this.data,
                layout: "fitColumns",
                layoutColumnsOnNewData: true,
                renderHorizontal: "virtual",
                columns: [
                    {
                        title: trans.payments.date,
                        field: "date",
                        resizable: "header",
                        minWidth: 170,
                        sorter: "string",
                        headerSort: false,
                        frozen: true,
                    },
                    {
                        title: trans.payments.state,
                        field: "state",
                        minWidth: 130,
                        resizable: "header",
                        headerSort: false,
                        formatter: function (cell) {
                            const value = cell.getValue();
                            if (value == null) return "";

                            return PaymentStateBadge.render(cell.getValue());
                        },
                    },
                    {
                        title: trans.orders.cancellationState,
                        field: "cancellationState",
                        minWidth: 130,
                        resizable: "header",
                        formatter: function (cell) {
                            const cancellationState = cell.getValue();
                            if (cancellationState == null) {
                                return "";
                            } else {
                                return CancellationStateBadge.render(cancellationState);
                            }
                        },
                    },
                    {
                        title: trans.payments.tableName,
                        field: "tableName",
                        minWidth: 130,
                        resizable: "header",
                        headerSort: false,
                        formatter: function (cell) {
                            const cellValue = cell.getValue();
                            const id = cell.getData().id;
                            if (id === "loading") {
                                return '<div class="loader green" style="margin: auto;" />';
                            } else {
                                return cellValue;
                            }
                        },
                    },
                    {
                        title: trans.payments.orders,
                        field: "orders",
                        resizable: "header",
                        minWidth: 100,
                        headerSort: false,
                        formatter: function (cell) {
                            const orders = cell.getValue();
                            const container = $(
                                '<div style="display: flex; flex-wrap: wrap; flex-direction: row;" </div>'
                            ).appendTo(cell.getElement());

                            for (let index = 0; index < orders.length; index++) {
                                const order = orders[index];
                                let label = order.number;
                                if (order.orderIncrement != null && order.orderIncrement != "") {
                                    label = order.orderIncrement;
                                }

                                const $order = $(
                                    '<a href="" style="margin-right: 4px;">' +
                                        label +
                                        (index < orders.length - 1 ? "," : "") +
                                        "</a>"
                                ).appendTo(container);

                                $order.click((e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    new OrderDetailsModal(orders[index].id, () => {
                                        that.refreshDataWithDelay();
                                    }).show();
                                });
                            }
                            return cell.getElement().lastElementChild;
                        },
                    },
                    {
                        title: trans.payments.totalIncTip,
                        field: "totalIncTip",
                        hozAlign: "right",
                        minWidth: 140,
                        resizable: "header",
                        headerSort: false,
                    },
                    {
                        title: trans.payments.paymentMethod,
                        field: "paymentMethod",
                        minWidth: 140,
                        resizable: "header",
                        headerSort: false,
                        formatter: function (cell) {
                            const value = cell.getValue() as PaymentMethodCell;
                            if (value == null) return "";

                            return PaymentMethodUtil.generatePaymentMethodDivIconFromPaymentMethodCell(trans.general.unknown, value);
                        },
                    },
                    {
                        title: trans.orders.user,
                        field: "userName",
                        resizable: "header",
                        minWidth: 160,
                        hozAlign: "right",
                        sorter: "string",
                        widthGrow: 1,
                        headerSort: false,
                        formatter: function (cell) {
                            const cellValue = cell.getValue();
                            if (cellValue == null) return trans.general.deletedUser;
                            else return cellValue == "Anonymer User" ? trans.orders.anonymousUser : cellValue;
                        },
                    },
                ],
            });

            this.table.on("scrollVertical", () => {
                if (!this.hasNextPage) {
                    return;
                }

                if (this.$scrollableTable != null) {
                    const bottom = this.$scrollableTable?.scrollTop() + this.$scrollableTable?.innerHeight();
                    const scrollHeight = this.$scrollableTable[0].scrollHeight - 40;
                    if (bottom >= scrollHeight) {
                        this.reloadData(this.currentPage + 1);
                    }
                }
            });

            this.table.on("rowClick", (e, row) => {
                const rowData = row.getData();
                this.openTransactionWindow(rowData.paymentId);
            });

            this.table.on("tableBuilt", () => {
                this.$scrollableTable = $("#payments-table > .tabulator-tableholder");

                this.reloadData();
            });
        } else {
            this.table.setData(this.data);
        }
    }

    private openTransactionWindow(transactionId: string) {
        const window = new TransactionDetailsWindow(transactionId);
        window.show();
    }

    private mapPayment(transaction: ITransactionMethodSplit): PaymentTableRow {
        const date = DateTime.toPreciseDateTimeString(transaction.confirmDate ?? transaction.createdDateTime);
        const orderNumbers = transaction.transactionOrders.map(
            (ord) => new PaymentOrderCell(ord.orderId, ord.orderNumber, ord.dailyOrderIncrement)
        );

        const totalIncTip = toMoneyWithSign(transaction.amount, true);
        const paymentMethod = new PaymentMethodCell(
            transaction.state,
            transaction.provider,
            transaction.brand,
            transaction.code,
            transaction.region,
            transaction.holder,
            transaction.ccDigits,
            transaction.ccExpiryYear,
            transaction.ccExpiryMonth,
            transaction.customPaymentMethodImagePath,
            transaction.predefinedPaymentMethodImagePath,
            transaction.actualPaymentMethodImagePath
        );

        return new PaymentTableRow(
            transaction.id,
            transaction.paymentId,
            date,
            transaction.state,
            orderNumbers,
            transaction.tableName ?? trans.production.deletedTable,
            totalIncTip,
            paymentMethod,
            transaction.cancellationState,
            transaction.userName
        );
    }

    private reloadData(pageNumber = 0) {
        if (this.isDataLoading) {
            return;
        }

        if (pageNumber == 0) {
            this.addLoadingRow();
            this.data = [];
        }

        this.isDataLoading = true;
        this.orderDao
            .getTransactionMethodSplits({
                locationId: OperatorClient.context.settings!.location!.id,
                limit: this.itemsPageSize,
                term: this.searchedOrderName,
                offset: pageNumber * this.itemsPageSize,
                createdFromLastBusinessDays: this.createdFromLastBusinessDays,
            })
            .then((result) => {
                if (this.table == null) {
                    logWarning("Table is not initialized, so payments cannot be loaded.");
                    return;
                }

                this.hasNextPage = result.hasNext;
                this.currentPage = pageNumber;

                const mappedPayments = result.items.map(this.mapPayment);

                this.data = [...this.data, ...mappedPayments];

                this.table.deleteRow("loading");

                if (pageNumber == 0) {
                    this.table.setData(mappedPayments);
                } else {
                    this.table.addData(mappedPayments, false);
                }

                if (this.hasNextPage) {
                    this.addLoadingRow();
                }
            })
            .catch((ex) => {
                logError(ex);
                Popup.show({
                    title: trans.general.failed,
                    message: trans.general.operationFailed,
                    uniqueId: "operation-failed-popup",
                });

                this.table.deleteRow("loading");
            })
            .finally(() => {
                this.isDataLoading = false;
            });
    }

    private addLoadingRow() {
        if (this.table != null) {
            const loader = new PaymentTableRow("loading", "", "", undefined, [], "", "", undefined, undefined, "");
            this.table.addData(loader, false);
        }
    }
}
